import { makeStyles } from '@material-ui/core';

// Vocabulary matched to the rest of Project Development (BatchGenealogy/Styles.js,
// ProjectAnalytics/Styles.js): #173072 brand navy, #E0E0E0 borders, #F9F9FC
// fills, 4px radii, 0.875rem body text. Only the bar colours are meaningful and
// those come from ganttModel's status/hierarchy tables.
// Two text lines (name+pill, then the sub-label) have to fit inside a row, and
// the row height must stay CONSTANT — GanttGrid positions its tooltip from
// rowIndex * ROW_H, so a variable-height row would misplace it.
export const ROW_H = 52;
export const LABEL_COL_W = 420;
export const HEADER_H = 56;

export const useStyles = makeStyles((theme) => ({
  container: { padding: theme.spacing(2.5, 4), fontFamily: theme.typography.fontFamily },

  // ── screen header ─────────────────────────────────────────────────────────
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(2), marginBottom: theme.spacing(2) },
  headerLeft: { display: 'flex', alignItems: 'center', gap: theme.spacing(2), minWidth: 0 },
  backButton: { display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#000', '&:hover': { opacity: 0.75 } },
  title: { fontSize: '1.25rem', fontWeight: 600, color: '#173072', whiteSpace: 'nowrap' },

  // CMO / Site selectors + Last Synced / Refresh (FSD A).
  facilityRow: {
    display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap',
    gap: theme.spacing(2.5), padding: theme.spacing(1.75, 2.5),
  },
  // Facility selectors and chart controls share one bordered panel. They used
  // to be a card and a bare strip stacked with a gap between them, which spent
  // ~50px of height on nothing and read as two unrelated toolbars.
  controlPanel: {
    border: '1px solid #E4E7EC', borderRadius: 8, background: '#F9FAFB',
    marginBottom: theme.spacing(2), overflow: 'hidden',
  },
  // Bold 1rem, the same as the column heading on the grid below and as the
  // Executive Summary's filter labels — the two PD screens read as one set.
  // Design (Post-Feedback UI Changes, Gantt slide): the ribbon's labels sit
  // INLINE to the left of their dropdown with a colon — "CMO:", "Site / Plant:"
  // — not stacked above it the way the dashboard's own filter bar does. That is
  // what keeps this ribbon to a single compact row.
  fieldLabel: { fontSize: '1rem', fontWeight: 'bold', color: '#344054', whiteSpace: 'nowrap' },
  fieldRow: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  field: { minWidth: 230 },
  syncWrap: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) },
  syncText: { fontSize: '0.75rem', color: '#667085', whiteSpace: 'nowrap' },
  // Orange in the design, matching the app's accent (same as Edit Project's EDIT).
  refreshButton: { textTransform: 'none', fontSize: '0.8125rem', color: '#ec6535', fontWeight: 600, minWidth: 0 },

  // ── breadcrumb + toolbar ──────────────────────────────────────────────────
  breadcrumb: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.5), fontSize: '0.875rem', marginBottom: theme.spacing(1) },
  crumbLink: { color: '#173072', fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit', '&:hover': { textDecoration: 'underline' } },
  crumbSep: { color: '#9A9AA2' },
  crumbCurrent: { color: '#333' },

  toolbarGroup: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.75) },
  // "ZOOM" in caps, per the design and FSD C's "a 'ZOOM' caption".
  toolbarLabel: { fontSize: '0.8125rem', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: 0.4 },

  // Zoom toggle — selected is navy fill / white text, the rest white / grey (FSD C).
  zoomButton: {
    textTransform: 'none', fontSize: '0.8125rem', minWidth: 78, padding: '7px 14px',
    border: '1px solid #D0D5DD', background: '#fff', color: '#475467', cursor: 'pointer',
    fontFamily: 'inherit',
    '&:first-child': { borderTopLeftRadius: 4, borderBottomLeftRadius: 4 },
    '&:last-child': { borderTopRightRadius: 4, borderBottomRightRadius: 4 },
    '&:not(:first-child)': { borderLeft: 'none' },
  },
  zoomButtonActive: { background: '#173072', color: '#fff', borderColor: '#173072' },

  // Status filter chips — an OFF chip is dimmed but still clickable (FSD D).

  textButton: { textTransform: 'none', fontSize: '0.8125rem', color: '#173072', minWidth: 0 },

  // Expand all / Collapse all — outlined pill buttons (FSD E).
  pillButton: {
    textTransform: 'none', fontSize: '0.8125rem', color: '#173072',
    borderColor: '#D0D5DD', borderRadius: 20, padding: '4px 14px', minWidth: 0,
  },

  // ── chart ─────────────────────────────────────────────────────────────────
  chartBox: { border: '1px solid #E4E7EC', borderRadius: 8, background: '#fff', overflow: 'hidden' },
  // The chart owns BOTH scroll axes. Vertically it used to grow without bound
  // and let the PAGE scroll, which carried the sticky timeline header off the
  // top - a fully expanded portfolio was rows of bars with no dates above them.
  // Now it scrolls inside a viewport-height box and the header stays put.
  chartScroll: { overflow: 'auto', position: 'relative' },
  chartInner: { position: 'relative' },

  headerRow: {
    display: 'flex', position: 'sticky', top: 0, zIndex: 3,
    background: '#F9FAFB', borderBottom: '1px solid #D0D5DD',
  },
  labelHeader: {
    width: LABEL_COL_W, flexShrink: 0, boxSizing: 'border-box',
    padding: '0 16px', display: 'flex', alignItems: 'center',
    height: HEADER_H, background: '#F9FAFB',
    borderRight: '1px solid #D0D5DD',
    position: 'sticky', left: 0, zIndex: 4,
    // The row-label column heading is a table column name, so it follows the
    // dashboard's headerCell (bold, 0.875rem) instead of the small tracked-out
    // caps it used before.
    // FSD F / G quote the heading in caps.
    fontSize: '1rem', fontWeight: 'bold', color: '#475467', textTransform: 'uppercase',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  // The band strip is NOT clipped: the Today pill is centred on its x
  // (translateX(-50%)) and would lose a half at either edge. Its position is
  // clamped in GanttGrid instead, so it cannot widen the scroll area either.
  bandsWrap: { position: 'relative', height: HEADER_H, flexShrink: 0 },
  bandMajor: { position: 'absolute', top: 0, height: HEADER_H / 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#344054', borderRight: '1px solid #D0D5DD', boxSizing: 'border-box', overflow: 'hidden', whiteSpace: 'nowrap' },
  bandMinor: { position: 'absolute', top: HEADER_H / 2, height: HEADER_H / 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#667085', borderRight: '1px solid #EAECF0', borderTop: '1px solid #EAECF0', boxSizing: 'border-box', overflow: 'hidden', whiteSpace: 'nowrap' },

  body: { position: 'relative' },
  row: { display: 'flex', height: ROW_H, alignItems: 'center', borderBottom: '1px solid #F2F4F7', background: '#fff' },
  // Four levels on one flat white list was impossible to scan. Each summary
  // level now carries its own tint (deeper = lighter) and a Project row opens
  // with a rule, so the eye finds where one project ends and the next begins.
  // This replaces the alternating-row stripe, which cut across the hierarchy
  // rather than showing it.
  rowProject: { background: '#EEF2FA', borderTop: '2px solid #C9D6EE' },
  rowCampaign: { background: '#F5F8FD' },
  rowStage: { background: '#FAFBFD' },
  labelCell: {
    width: LABEL_COL_W, flexShrink: 0, boxSizing: 'border-box',
    height: ROW_H, display: 'flex', alignItems: 'center', gap: 4,
    padding: '0 16px', background: 'inherit',
    borderRight: '1px solid #D0D5DD',
    position: 'sticky', left: 0, zIndex: 2,
    overflow: 'hidden',
  },
  // The name + status pill on one line, the sub-label beneath. Previously all
  // three sat on a single line and the name — the only element allowed to
  // shrink — was ellipsised down to a few characters by the pill and sub-label
  // beside it.
  labelStack: { display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2, flex: 1 },
  rowStatusDot: {
    width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginRight: 2,
  },
  labelLine: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 },
  twisty: {
    width: 22, height: 22, flexShrink: 0, padding: 0, border: 'none',
    borderRadius: 4, background: 'none', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', color: '#475467',
    '&:hover': { background: 'rgba(16,24,40,0.06)' },
  },
  twistySpacer: { width: 22, flexShrink: 0 },
  rowName: { fontSize: '0.875rem', color: '#101828', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.35 },
  rowNameProject: { fontWeight: 700 },
  rowNameCampaign: { fontWeight: 600 },
  rowNameDrillable: { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } },
  rowSub: { fontSize: '0.75rem', color: '#667085', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.35 },
  // Status pill beside the row name (FSD F Business Rules).
  rowStatusPill: { fontSize: '0.6875rem', fontWeight: 600, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap', flexShrink: 0 },

  // Each row's lane clips at its edge: its bars are absolutely positioned from
  // a rounded left + width, so one can sit a sub-pixel past the lane and widen
  // the scroll area — a horizontal scrollbar on a Quarter-zoom chart that
  // auto-fits and should never scroll (BR-07). FSD C prescribes the same for
  // the scrolling zooms: "bars running past the visible window are clipped at
  // its edge".
  lane: { position: 'relative', height: ROW_H, flexShrink: 0, overflow: 'hidden' },
  gridLineMinor: { position: 'absolute', top: 0, bottom: 0, width: 1, background: 'rgba(16,24,40,0.05)' },
  gridLineMajor: { position: 'absolute', top: 0, bottom: 0, width: 1, background: 'rgba(16,24,40,0.11)' },

  // Batch rows: full rounded block bar.
  // The inset hairline keeps the palest statuses (Planned's grey) from
  // disappearing into the row tint behind them.
  bar: { position: 'absolute', top: 11, height: ROW_H - 22, borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', cursor: 'default', boxShadow: 'inset 0 0 0 1px rgba(16,24,40,0.14)' },
  barText: { fontSize: '0.6875rem', fontWeight: 500, padding: '0 8px', whiteSpace: 'nowrap', pointerEvents: 'none' },
  // The same range as .barText, but sitting on the chart background instead of
  // on a coloured bar - so it takes the page's own ink rather than a contrast
  // colour, and must not catch the pointer the bar's tooltip belongs to.
  barTextOutside: {
    position: 'absolute', top: 0, height: ROW_H, display: 'flex', alignItems: 'center',
    fontSize: '0.6875rem', fontWeight: 500, color: '#667085',
    whiteSpace: 'nowrap', pointerEvents: 'none',
  },
  barDrillable: { cursor: 'pointer' },
  // In-progress elapsed overlay (FSD I): translucent white with a solid leading edge.
  elapsed: { position: 'absolute', top: 0, bottom: 0, left: 0, background: 'rgba(255,255,255,0.42)', borderRight: '2px solid rgba(255,255,255,0.95)', pointerEvents: 'none' },

  // Project/Campaign/Stage rows: slim bracket-style summary bar with end caps.
  summaryBar: { position: 'absolute', top: ROW_H / 2 - 5, height: 10, cursor: 'default' },
  summarySpine: { position: 'absolute', left: 0, right: 0, top: 2, height: 6, borderRadius: 3 },
  summaryCap: { position: 'absolute', top: 0, width: 4, height: 10, borderRadius: 2 },

  // Dashed vertical Today line + its pill in the header (FSD H).
  todayLine: { position: 'absolute', top: 0, bottom: 0, width: 0, borderLeft: '1px dashed #C62828', zIndex: 1, pointerEvents: 'none' },
  todayPill: { position: 'absolute', top: 4, transform: 'translateX(-50%)', background: '#C62828', color: '#fff', fontSize: '0.625rem', fontWeight: 700, borderRadius: 3, padding: '1px 6px', whiteSpace: 'nowrap', zIndex: 5, pointerEvents: 'none' },

  // ── tooltip (FSD J) ───────────────────────────────────────────────────────
  tooltip: {
    position: 'absolute', zIndex: 10, pointerEvents: 'none',
    background: '#fff', border: '1px solid #C6C6C6', borderRadius: 4,
    boxShadow: '0 2px 10px rgba(0,0,0,0.14)',
    padding: '8px 10px', minWidth: 200, maxWidth: 320,
  },
  tipHead: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 },
  tipTitle: { fontSize: '0.8125rem', fontWeight: 700, color: '#000', overflowWrap: 'anywhere' },
  tipRow: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: '0.75rem', lineHeight: 1.6 },
  tipKey: { color: '#6B6B6B', whiteSpace: 'nowrap' },
  tipVal: { color: '#000', textAlign: 'right' },
  tipDrill: { marginTop: 6, paddingTop: 6, borderTop: '1px solid #EEE', fontSize: '0.6875rem', fontWeight: 600, color: '#173072' },

  // ── states ────────────────────────────────────────────────────────────────
  state: {
    padding: theme.spacing(8), textAlign: 'center', color: '#667085', fontSize: '0.875rem',
    border: '1px solid #E4E7EC', borderRadius: 8, background: '#FCFCFD',
  },
}));
